import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../../../../../utils/api";
import ReportFormComponent from "./ReportFormComponent";
import FileSizeErrorModal from "./FileSizeErrorModal";
import { useToast } from "../../components/AdminSharedComponents";

const ReportForm = ({ isEdit = false }) => {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: "",
    excavation_point_id: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [pendingRemovals, setPendingRemovals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileSizeErrorModal, setFileSizeErrorModal] = useState({
    isOpen: false,
    invalidFiles: [],
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const excavationPointIdFromQuery = queryParams.get("excavation_point_id");

  const [isProjectLocked, setIsProjectLocked] = useState(false);
  const [isExcavationLocked, setIsExcavationLocked] = useState(false);

  // Prevent loading project twice
  const hasLoadedReport = useRef(false);

  // Fetch projects and report data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isEdit) setLoading(true); // hanya set loading awal kalau edit

        // Always fetch projects
        const projectsResponse = await api.get("/api/projects");
        setProjects(projectsResponse.data);

        if (isEdit && id && !hasLoadedReport.current) {
          const reportResponse = await api.get(`/api/reports/${id}`);
          const report = reportResponse.data;

          setFormData({
            title: report.title || "",
            description: report.description || "",
            project_id: report.project_id || "",
            excavation_point_id: report.excavation_point_id || "",
          });

          if (report.attachments) {
            setCurrentAttachments(report.attachments);
          }

          hasLoadedReport.current = true;
        } else if (!isEdit && projectId) {
          setFormData((prevData) => ({
            ...prevData,
            project_id: Number(projectId),
          }));
          setIsProjectLocked(true); // 🔒 lock project kalau dari showProject
        }

        // ✅ tambahan: auto isi project kalau ada di query
        const projectIdFromQuery = queryParams.get("project_id");
        if (!isEdit && projectIdFromQuery) {
          setFormData((prev) => ({
            ...prev,
            project_id: Number(projectIdFromQuery),
          }));
          setIsProjectLocked(true); // 🔒 lock project kalau dari query
        }

        // ✅ tambahan: auto isi excavation point kalau ada di query
        if (!isEdit && excavationPointIdFromQuery) {
          try {
            const pointResponse = await api.get(
              `/api/excavation-points/${excavationPointIdFromQuery}`
            );
            const point = pointResponse.data;

            setFormData((prev) => ({
              ...prev,
              excavation_point_id: point.id,
              project_id: point.project_id, // ikut project parent
            }));
            setIsProjectLocked(true); // 🔒 lock project
            setIsExcavationLocked(true); // 🔒 lock excavation
          } catch (err) {
            console.error("Failed to fetch excavation point:", err);
            toast.error("Excavation point not found");
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        const errorMessage = `Failed to load ${
          isEdit ? "report" : "projects"
        } data. Please try again.`;
        setError(errorMessage);
        toast.error(errorMessage);
        setLoading(false);

        if (isEdit) {
          navigate("/admin/reports");
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, projectId]);

  // Handle removing an existing attachment
  const handleRemoveAttachment = async (attachmentId) => {
    try {
      // Mark for removal instead of immediate deletion
      setPendingRemovals((prev) => [...prev, attachmentId]);
      toast.success("File marked for removal");
    } catch (error) {
      console.error("Error marking attachment for removal:", error);
      toast.error("Error marking file for removal");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle file input change
  const handleFileChange = (files) => {
    if (files.length === 0) return;

    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);

    // Create preview for each file
    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews((prevPreviews) => [
            ...prevPreviews,
            { file, preview: reader.result },
          ]);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreviews((prevPreviews) => [
          ...prevPreviews,
          { file, preview: null },
        ]);
      }
    });
  };

  // Handler to remove new file
  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFilePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index)
    );
  };

  // Handle existing attachment removal (mark for deletion)
  const handleExistingAttachmentRemoval = (attachmentId) => {
    setPendingRemovals((prev) => [...prev, attachmentId]);
  };

  // Handle undo removal for existing attachments
  const handleUndoRemoval = (attachmentId) => {
    setPendingRemovals((prev) => prev.filter((id) => id !== attachmentId));
  };

  // Handle file size error
  const handleFileSizeError = (invalidFiles) => {
    setFileSizeErrorModal({
      isOpen: true,
      invalidFiles: invalidFiles,
    });
  };

  // Close file size error modal
  const closeFileSizeErrorModal = () => {
    setFileSizeErrorModal({
      isOpen: false,
      invalidFiles: [],
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Report title is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setUploadProgress(0);

      // Create FormData object to handle file upload
      const reportFormData = new FormData();
      reportFormData.append("title", formData.title);
      reportFormData.append("description", formData.description);

      if (formData.project_id) {
        reportFormData.append("project_id", formData.project_id);
      }
      if (formData.excavation_point_id) {
        reportFormData.append(
          "excavation_point_id",
          formData.excavation_point_id
        );
      }

      // Append new files if selected
      if (selectedFiles && selectedFiles.length > 0) {
        selectedFiles.forEach((file) => {
          reportFormData.append("attachments", file);
        });
      }

      // Add removed attachment IDs if any (for edit mode)
      if (isEdit && pendingRemovals.length > 0) {
        reportFormData.append(
          "removedAttachments",
          JSON.stringify(pendingRemovals)
        );
      }

      // Configure request with upload progress and timeout
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5 minutes timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      if (isEdit) {
        // Update the report
        await api.put(`/api/reports/${id}`, reportFormData, config);
        toast.success("Report updated successfully!");
      } else {
        // Create new report
        await api.post("/api/reports", reportFormData, config);
        toast.success("Report created successfully!");
      }

      setSubmitting(false);
      setUploadProgress(0);

      // Clean up blob URLs after successful submission
      if (filePreviews && filePreviews.length > 0) {
        filePreviews.forEach((preview) => {
          if (preview.preview) {
            URL.revokeObjectURL(preview.preview);
          }
        });
      }

      navigate("/admin/reports");
    } catch (error) {
      console.error(`Error ${isEdit ? "updating" : "creating"} report:`, error);

      let errorMessage;
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        errorMessage =
          "Upload timeout. Please try again with smaller files or check your connection.";
      } else if (error.response?.status === 413) {
        errorMessage =
          "File too large. Please reduce file sizes and try again.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = `Failed to ${
          isEdit ? "update" : "create"
        } report. Please try again.`;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    navigate("/admin/reports");
  };

  // Show loading state
  if (loading && (isEdit || projects.length === 0)) {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-50 bg-gray-50">
        <div className="animate-pulse text-lg font-semibold text-primary">
          Loading {isEdit ? "report" : "projects"} data...
        </div>
      </div>
    );
  }

  return (
    <>
      <ReportFormComponent
        formId="report-form"
        isEdit={isEdit}
        loading={loading}
        submitting={submitting}
        title={isEdit ? "Edit Report" : "Add New Report"}
        backLink="/admin/reports"
        backText="Back to Reports"
        cancelLink={isEdit ? `/admin/reports/show/${id}` : undefined}
        cancelText={isEdit ? "Cancel" : undefined}
        submitText={isEdit ? "Update Report" : "Create Report"}
        loadingText={isEdit ? "Updating..." : "Creating..."}
        submitIcon={null}
        error={error}
        uploadProgress={uploadProgress}
        formData={formData}
        setFormData={setFormData}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        filePreviews={filePreviews}
        setFilePreviews={setFilePreviews}
        currentAttachments={currentAttachments}
        onRemoveAttachment={handleRemoveAttachment}
        projects={projects}
        isProjectLocked={isProjectLocked}
        isExcavationLocked={isExcavationLocked}
        pendingRemovals={pendingRemovals}
        setPendingRemovals={setPendingRemovals}
        handleChange={handleChange}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
        handleExistingAttachmentRemoval={handleExistingAttachmentRemoval}
        handleUndoRemoval={handleUndoRemoval}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        onFileSizeError={handleFileSizeError}
      />

      {/* File Size Error Modal */}
      <FileSizeErrorModal
        isOpen={fileSizeErrorModal.isOpen}
        onClose={closeFileSizeErrorModal}
        maxSize={10}
        invalidFiles={fileSizeErrorModal.invalidFiles}
      />
    </>
  );
};

export default ReportForm;
